import { BrowserRouter, Routes, Route, Outlet, Navigate } from "react-router-dom";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import LocationPage from "./pages/LocationPage";
import TeacherTraining from "./pages/TeacherTraining";
import OnlineCourses from "./pages/OnlineCourses";
import PrenatalYoga from "./pages/PrenatalYoga";
import TeacherOfferingDetailPage from "./pages/TeacherOfferingDetailPage";
import TeacherInstructorDetailPage from "./pages/TeacherInstructorDetailPage";
import ProviderCoursesPage from "./pages/ProviderCoursesPage";
import TeacherProfilePage from "./pages/TeacherProfilePage";
import RetreatDetailPage from "./pages/RetreatDetailPage";
import PartnershipAgreement from "./pages/PartnershipAgreement";
import BecomeInstructorPage from "./pages/BecomeInstructorPage";
import BecomePartnerPage from "./pages/BecomePartnerPage";
import LoginPage from "./pages/auth/LoginPage";
import SignupPage from "./pages/auth/SignupPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import VerifyEmailPage from "./pages/auth/VerifyEmailPage";
import InstructorShell from "./components/instructor/InstructorShell";
import PartnerShell from "./components/partner/PartnerShell";
import { RequireAuth } from "./components/instructor/RequireAuth";
import InstructorDashboard from "./pages/instructor/InstructorDashboard";
import BasicsStep from "./pages/instructor/onboarding/BasicsStep";
import ExpertiseStep from "./pages/instructor/onboarding/ExpertiseStep";
import CredentialsStep from "./pages/instructor/onboarding/CredentialsStep";
import MediaStep from "./pages/instructor/onboarding/MediaStep";
import OfferingsStep from "./pages/instructor/onboarding/OfferingsStep";
import ReviewStep from "./pages/instructor/onboarding/ReviewStep";
import PartnerDashboard from "./pages/partner/PartnerDashboard";
import PartnerBasicsStep from "./pages/partner/onboarding/BasicsStep";
import PartnerSourceStep from "./pages/partner/onboarding/SourceStep";
import PartnerMediaStep from "./pages/partner/onboarding/MediaStep";
import PartnerCommercialStep from "./pages/partner/onboarding/CommercialStep";
import PartnerReviewStep from "./pages/partner/onboarding/ReviewStep";
import AdminApplicationsPage from "./pages/admin/AdminApplicationsPage";
import AdminApplicationDetailPage from "./pages/admin/AdminApplicationDetailPage";
import AdminPartnerApplicationsPage from "./pages/admin/AdminPartnerApplicationsPage";
import AdminPartnerApplicationDetailPage from "./pages/admin/AdminPartnerApplicationDetailPage";
import { AuthProvider } from "./context/AuthContext";
import { CurrencyProvider } from "./context/CurrencyContext";
import { ONLINE_COURSE, PRENATAL_YOGA } from "./lib/courseTypes";

function MarketingLayout() {
  return (
    <>
      <Header />
      <Outlet />
      <Footer />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <CurrencyProvider>
        <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route
            path="/verify-email"
            element={
              <RequireAuth>
                <VerifyEmailPage />
              </RequireAuth>
            }
          />

          <Route
            path="/instructor"
            element={
              <RequireAuth>
                <InstructorShell />
              </RequireAuth>
            }
          >
            <Route index element={<InstructorDashboard />} />
            <Route path="onboarding/basics" element={<BasicsStep />} />
            <Route
              path="onboarding/about"
              element={<Navigate to="/instructor/onboarding/expertise" replace />}
            />
            <Route path="onboarding/expertise" element={<ExpertiseStep />} />
            <Route path="onboarding/credentials" element={<CredentialsStep />} />
            <Route path="onboarding/media" element={<MediaStep />} />
            <Route path="onboarding/offerings" element={<OfferingsStep />} />
            <Route path="onboarding/review" element={<ReviewStep />} />
          </Route>

          <Route
            path="/partner"
            element={
              <RequireAuth>
                <PartnerShell />
              </RequireAuth>
            }
          >
            <Route index element={<PartnerDashboard />} />
            <Route path="onboarding/basics" element={<PartnerBasicsStep />} />
            <Route path="onboarding/source" element={<PartnerSourceStep />} />
            <Route path="onboarding/media" element={<PartnerMediaStep />} />
            <Route path="onboarding/commercial" element={<PartnerCommercialStep />} />
            <Route path="onboarding/review" element={<PartnerReviewStep />} />
          </Route>

          <Route
            path="/admin/applications"
            element={
              <RequireAuth role="admin">
                <AdminApplicationsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/applications/:id"
            element={
              <RequireAuth role="admin">
                <AdminApplicationDetailPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/partner-applications"
            element={
              <RequireAuth role="admin">
                <AdminPartnerApplicationsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/partner-applications/:id"
            element={
              <RequireAuth role="admin">
                <AdminPartnerApplicationDetailPage />
              </RequireAuth>
            }
          />

          <Route element={<MarketingLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/yoga-teacher-training" element={<TeacherTraining />} />
            <Route
              path="/online-courses/provider/:source"
              element={<ProviderCoursesPage listingType={ONLINE_COURSE} />}
            />
            <Route
              path="/online-courses/instructor/:slug"
              element={<TeacherInstructorDetailPage listingType={ONLINE_COURSE} />}
            />
            <Route
              path="/online-courses/:slug"
              element={<TeacherOfferingDetailPage listingType={ONLINE_COURSE} />}
            />
            <Route path="/online-courses" element={<OnlineCourses />} />
            <Route
              path="/prenatal-yoga/provider/:source"
              element={<ProviderCoursesPage listingType={PRENATAL_YOGA} />}
            />
            <Route
              path="/prenatal-yoga/instructor/:slug"
              element={<TeacherInstructorDetailPage listingType={PRENATAL_YOGA} />}
            />
            <Route
              path="/prenatal-yoga/:slug"
              element={<TeacherOfferingDetailPage listingType={PRENATAL_YOGA} />}
            />
            <Route path="/prenatal-yoga" element={<PrenatalYoga />} />
            <Route path="/teachers/:slug" element={<TeacherProfilePage />} />
            <Route path="/yoga-retreats-in/:slug" element={<LocationPage />} />
            <Route path="/retreats/:slug" element={<RetreatDetailPage />} />
            <Route path="/about" element={<About />} />
            <Route path="/become-an-instructor" element={<BecomeInstructorPage />} />
            <Route path="/become-a-partner" element={<BecomePartnerPage />} />
            <Route path="/partnership-agreement" element={<PartnershipAgreement />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
          </Route>
        </Routes>
      </BrowserRouter>
      </CurrencyProvider>
    </AuthProvider>
  );
}

export default App;
